import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useT } from '~/i18n';
import { WaitingInline } from '~/components/ui';
import ScreeningWizard from './components/ScreeningWizard';
import ScreeningReview from './components/ScreeningReview';
import { fetchProfile } from './api/profileClient';
import { ensureManagedMembership } from './api/onboardingClient';
import { saveWhatsappPhone } from './api/englishClient';
import {
  ScreeningError,
  createScreeningCV,
  fetchScreeningAnswers,
  makeCVPrimary,
  regenerateScreeningCV,
  type ScreeningCVResult,
} from './api/screeningClient';
import {
  SCREENING_STEPS,
  clearSession,
  emptyDraft,
  fromAnswers,
  issuesToErrors,
  loadSession,
  saveSession,
  stepForField,
  stepIndex,
  toAnswers,
  toScreeningMetadata,
  validateDraft,
  type FieldErrors,
  type ScreeningDraft,
} from './lib/screening';

/**
 * Cambiar de paso o pasar a la revisión deja la vista donde estaba, que en un
 * teléfono es a media pantalla del contenido nuevo.
 *
 * Se escribe el `scrollTop` en vez de llamar `window.scrollTo()` porque jsdom no
 * implementa ese método y cada llamada ensucia la salida de las pruebas con un
 * "Not implemented" que no es un problema de nadie.
 */
function scrollToTop(): void {
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

/**
 * La segunda puerta de la hoja de vida: la que no pide archivo.
 *
 * Quien aplica a una H-2B casi nunca tiene un CV en PDF —y si lo tiene, suele
 * ser un documento en español pensado para un empleador local, que no es lo que
 * lee el DOL. Acá contesta un cuestionario en su idioma y sale con una hoja de
 * vida en inglés, en formato ATS, que además queda indexada para el matching
 * exactamente igual que una que se hubiera subido.
 *
 * ── DÓNDE NACE LA MEMBRESÍA ────────────────────────────────────────────────
 * El cuestionario se puede llenar entero sin cuenta de envío: el alta del canal
 * gestionado se dispara recién cuando hay algo que guardar. Como el servidor
 * responde `no_membership` en ese caso exacto, no hace falta preguntarle antes
 * si existe — se activa y se reintenta una vez. Un GET menos en el camino más
 * caliente del alta.
 */
export default function JobApplyScreeningPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useT('screening');

  // Una sola lectura del avance guardado, al montar: después manda el estado.
  const restored = useMemo(() => loadSession(), []);

  const [draft, setDraft] = useState<ScreeningDraft>(restored?.draft ?? emptyDraft());
  const [step, setStep] = useState<number>(restored?.step ?? 0);
  const [result, setResult] = useState<ScreeningCVResult | null>(restored?.result ?? null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [promoting, setPromoting] = useState(false);
  /**
   * La hoja de vida que se está MEJORANDO. Se llena al volver al cuestionario
   * desde la revisión ("Agregar estos datos", "Volver a mis respuestas") —o
   * desde el perfil, que entra con `?replaces=`— y viaja al recrearla: el
   * servidor hace que la nueva ocupe su lugar en vez de quedar como una
   * paralela que nadie usa para postular.
   */
  const [replacesCvId, setReplacesCvId] = useState<string | null>(
    // El enlace del perfil gana sobre lo guardado: es la navegación más reciente.
    () => searchParams.get('replaces') ?? restored?.replaces_cv_id ?? null,
  );

  /**
   * Quien llega del perfil a mejorar su hoja de vida OTRO día no trae borrador:
   * vive en sessionStorage y esa sesión ya no existe. Sin esto el cuestionario
   * abría vacío y para sumar un dato había que contestarlo todo otra vez
   * («le dan en el más y no pasa nada»). Se traen las respuestas con que se
   * escribió y se abre en Tu experiencia, que es donde cae casi todo lo que le
   * falta (tareas, cantidades, equipos); las demás quedan a un «Atrás».
   */
  const [prefilling, setPrefilling] = useState(
    () => !!replacesCvId && !(restored?.replaces_cv_id === replacesCvId && restored.draft.full_name.trim()),
  );
  useEffect(() => {
    if (!prefilling || !replacesCvId) return;
    let alive = true;
    fetchScreeningAnswers(replacesCvId).then((answers) => {
      if (!alive) return;
      if (answers) {
        setDraft(fromAnswers(answers));
        setStep(stepIndex('experience'));
      }
      setPrefilling(false);
    });
    return () => {
      alive = false;
    };
    // Una sola vez al montar: es la lectura inicial, después manda el estado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El `?replaces=` se CONSUME: ya quedó en el estado (y de ahí en la sesión),
  // y dejarlo en la URL haría que una recarga lo volviera a imponer aunque ya
  // se haya creado otra hoja de vida y la que se está mejorando sea ésa.
  useEffect(() => {
    if (!searchParams.has('replaces')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('replaces');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // El avance se guarda en cada cambio. Una recarga —o el navegador matando la
  // pestaña para recuperar memoria, que en un teléfono barato pasa— devuelve a
  // la persona al paso donde estaba, con lo que ya había escrito.
  useEffect(() => {
    saveSession({ draft, step, result, replaces_cv_id: replacesCvId });
  }, [draft, step, result, replacesCvId]);

  const jumpToFirstError = useCallback((fieldErrors: FieldErrors) => {
    const first = Object.keys(fieldErrors)[0];
    if (!first) return;
    const target = stepForField(first);
    if (target) setStep(stepIndex(target));
  }, []);

  const submit = useCallback(async () => {
    const invalid = validateDraft(draft);
    if (Object.keys(invalid).length) {
      setErrors(invalid);
      jumpToFirstError(invalid);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    const answers = toAnswers(draft);
    // Cómo contestó cada pregunta abierta —hablando o escribiendo, y qué tan
    // rica salió la respuesta— viaja junto al cuestionario. Es la única forma de
    // llegar a saber si quien habla coloca más que quien escribe.
    const metadata = toScreeningMetadata(draft);

    try {
      let created: ScreeningCVResult;
      try {
        created = await createScreeningCV(answers, metadata, replacesCvId);
      } catch (err) {
        // La única falla que se arregla sola: todavía no hay membresía porque el
        // alta se difirió hasta este momento. Se activa el canal y se reintenta
        // UNA vez — si vuelve a fallar, es otra cosa y se cuenta.
        if (err instanceof ScreeningError && err.code === 'no_membership') {
          await ensureManagedMembership();
          created = await createScreeningCV(answers, metadata, replacesCvId);
        } else {
          throw err;
        }
      }

      // El teléfono del cuestionario ES su WhatsApp (2026-10-08): se guarda
      // también en la membresía, que es de donde lo leen los avisos y la
      // evaluación de inglés. Ya hay membresía (la hoja de vida se guardó). Si
      // falla, la hoja de vida no se pierde por eso: queda Perfil.
      if (answers.phone) await saveWhatsappPhone(answers.phone).catch(() => undefined);

      // El reemplazo ya se hizo: la que se mejora de acá en adelante es ésta, y
      // la apunta `backToForm` si la persona vuelve otra vez.
      setReplacesCvId(null);
      setResult(created);
      setErrors({});
      setReviewError(null);
      scrollToTop();
    } catch (err) {
      if (err instanceof ScreeningError && err.code === 'invalid_screening' && err.issues.length) {
        const fieldErrors = issuesToErrors(err.issues);
        setErrors(fieldErrors);
        jumpToFirstError(fieldErrors);
        setSubmitError(t.page.checkMarked);
      } else {
        setSubmitError(err instanceof Error ? err.message : t.page.submitFailed);
      }
    } finally {
      setSubmitting(false);
    }
  }, [draft, jumpToFirstError, replacesCvId, t]);

  const regenerate = useCallback(
    async (feedback: string) => {
      if (!result) return;
      setRegenerating(true);
      setReviewError(null);
      try {
        const next = await regenerateScreeningCV(result.cv_id, feedback);
        // `is_primary` no viene en la regeneración: es el mismo CV, así que se
        // conserva el que se supo al crearlo.
        setResult({ ...next, is_primary: result.is_primary });
        scrollToTop();
      } catch (err) {
        if (err instanceof ScreeningError && err.code === 'regeneration_limit') {
          setResult({ ...result, regenerations_left: 0 });
        }
        setReviewError(err instanceof Error ? err.message : t.page.regenerateFailed);
      } finally {
        setRegenerating(false);
      }
    },
    [result, t],
  );

  const confirm = useCallback(() => {
    // El borrador ya cumplió: la hoja de vida está guardada del lado del
    // servidor y volver a este formulario crearía una segunda.
    clearSession();
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  /**
   * "Usar esta hoja de vida": la persona ya postulaba con otra (un PDF, casi
   * siempre) y elige la del cuestionario. Es la única forma de cambiarla sin
   * subir un archivo, y por eso se ofrece justo acá y no se hace sola.
   */
  const chooseThisOne = useCallback(async () => {
    if (!result) return;
    setPromoting(true);
    setReviewError(null);
    try {
      await makeCVPrimary(result.cv_id);
      clearSession();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : t.page.submitFailed);
      setPromoting(false);
    }
  }, [navigate, result, t]);

  const backToForm = useCallback(() => {
    // Lo que vuelva a salir del cuestionario MEJORA esta hoja de vida: la
    // reemplaza y se queda con su lugar. Ver `replacesCvId`.
    if (result) setReplacesCvId(result.cv_id);
    setResult(null);
    setReviewError(null);
    setStep(SCREENING_STEPS.length - 1);
    scrollToTop();
  }, [result]);

  /**
   * «Agregar estos datos»: el envío ya creó (o actualizó) el perfil por piezas,
   * así que lo que falta se contesta en Mejorar mi hoja de vida, una carencia a
   * la vez, y no rehaciendo el cuestionario. El borrador ya cumplió, como al
   * confirmar.
   */
  const improve = useCallback(() => {
    clearSession();
    navigate('/perfil/hoja-de-vida/mejorar');
  }, [navigate]);

  /**
   * ¿Hay perfil por piezas? Con perfil, «Volver a mis respuestas» desaparece
   * (D10): los datos se agregan en Mejorar y se corrigen en Mis datos. Sin
   * perfil (o si la lectura falla) se ve como siempre. Mientras se pregunta,
   * escondido: aparecer y desaparecer en un segundo es peor que llegar tarde.
   */
  const [profileKnown, setProfileKnown] = useState<'pending' | 'yes' | 'no'>('pending');
  const reviewing = result?.cv_id ?? null;
  const hasProfile = reviewing ? profileKnown !== 'no' : false;
  useEffect(() => {
    if (!reviewing) return;
    let alive = true;
    setProfileKnown('pending');
    fetchProfile().then((state) => {
      if (alive) setProfileKnown(state.status === 'ok' ? 'yes' : 'no');
    });
    return () => {
      alive = false;
    };
  }, [reviewing]);

  const exit = useCallback(() => {
    navigate('/onboarding?step=cv');
  }, [navigate]);

  if (prefilling) return <WaitingInline label={t.page.prefilling} />;

  if (result) {
    return (
      <ScreeningReview
        result={result}
        onConfirm={confirm}
        confirming={false}
        onUseThis={chooseThisOne}
        promoting={promoting}
        onRegenerate={regenerate}
        regenerating={regenerating}
        error={reviewError}
        onBackToForm={backToForm}
        onAddMissing={result.cv_id ? improve : undefined}
        hasProfile={hasProfile}
      />
    );
  }

  return (
    <ScreeningWizard
      draft={draft}
      onDraftChange={setDraft}
      step={step}
      onStepChange={setStep}
      errors={errors}
      onErrorsChange={setErrors}
      submitting={submitting}
      submitError={submitError}
      onSubmit={submit}
      onExit={exit}
    />
  );
}
